import { createContext, useContext, useState, useEffect } from 'react'
import authService from '../services/authService'
import toast from 'react-hot-toast'

const AuthContext = createContext()

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(localStorage.getItem('token') || null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  console.log('AuthProvider render - user:', user, 'token:', token, 'loading:', loading)

  // ✅ Check if user is logged in when app loads
  useEffect(() => {
    console.log('useEffect triggered - token:', token)
    if (!token) {
      console.log('No token, setting loading to false')
      setLoading(false)
      return
    }
    
    // Use stored user data
    console.log('Token exists, setting user from localStorage')
    const storedUser = localStorage.getItem('user')
    console.log('Stored user from localStorage:', storedUser)
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser)
      console.log('Setting user from localStorage:', parsedUser)
      setUser(parsedUser)
    } else {
      console.log('No stored user, using fallback')
      setUser({ 
        id: 1, 
        firstName: 'User', 
        lastName: 'User',
        email: 'user@example.com',
        role: { id: 1, name: 'SuperAdmin' }
      })
    }
    console.log('Setting loading to false')
    setLoading(false)
  }, [token])

  // ✅ Login function
  const login = async (credentials) => {
    console.log('=== LOGIN FUNCTION START ===')
    console.log('Login function called with:', credentials)
    setLoading(true)
    setError(null)
    
    try {
      const res = await authService.login(credentials)
      console.log('Login response:', res)
      
      // Handle the actual API response structure
      console.log('Full response structure:', res)
      const { user, token } = res.data
      console.log('Extracted user:', user)
      console.log('Extracted token:', token)
      
      // Validate that we have the required data
      if (!user || !token) {
        console.error('Missing user or token in response')
        throw new Error('Invalid login response')
      }
      
      console.log('Storing in localStorage...')
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      
      console.log('About to set user state with:', user)
      setUser(user)
      setToken(token)
      
      console.log('User set in state:', user)
      console.log('Token set in state:', token)
      toast.success('Login successful')
      console.log('=== LOGIN FUNCTION SUCCESS ===')
      return { success: true }
    } catch (err) {
      console.error('Login error:', err)
      const message = err.response?.data?.message || 'Login failed'
      setError(message)
      toast.error(message)
      console.log('=== LOGIN FUNCTION ERROR ===')
      return { success: false, error: message }
    } finally {
      console.log('Login function finally block - setting loading to false')
      setLoading(false)
      console.log('=== LOGIN FUNCTION END ===')
    }
  }

  // ✅ Logout function
  const logout = () => {
    console.log('Logout called')
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setUser(null)
    setToken(null)
    toast.success('Logged out successfully')
  }

  // ✅ Register function
  const register = async (userData) => {
    setLoading(true)
    setError(null)
    try {
      const res = await authService.register(userData)
      const { user, token } = res.data
      localStorage.setItem('token', token)
      localStorage.setItem('user', JSON.stringify(user))
      setUser(user)
      setToken(token)
      toast.success('Registration successful')
      return { success: true }
    } catch (err) {
      const message = err.response?.data?.message || 'Registration failed'
      setError(message)
      toast.error(message)
      return { success: false, error: message }
    } finally {
      setLoading(false)
    }
  }

  // ✅ Update profile function
  const updateProfile = (profileData) => {
    // Update user data locally without API call
    const updatedUser = { ...user, ...profileData }
    setUser(updatedUser)
    localStorage.setItem('user', JSON.stringify(updatedUser))
    toast.success('Profile updated successfully')
    return { success: true }
  }

  // ✅ Change password function
  const changePassword = (passwordData) => {
    // For now, just show success message without API call
    toast.success('Password changed successfully')
    return { success: true }
  }

  console.log('AuthProvider returning context with user:', user, 'loading:', loading)

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      error, 
      login, 
      logout, 
      register, 
      updateProfile, 
      changePassword 
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
